import { Box, Divider, Stack, Tab, Tabs } from "@mui/material";
import { useState } from "react";
import { useSelector } from "react-redux";
import ScrollableFlexContainer from "@entrilia/shared/components/ScrollableFlexContainer.tsx";
import type { DimensionDescriptor } from "../../../../../../../api/types/svTypes.ts";
import { useBiCommonDataContext } from "../../../contexts/BiCommonDataContext.ts";
import { useAppDispatch } from "../../../store/hooks/useAppDispatch.ts";
import { selectDimensionsStructureForCurrentReportType } from "../../../store/selectors/metaDataSelectors.ts";
import { selectEditDisabled, selectSelectedBuilderTab } from "../../../store/selectors/uiSettingsSelectors.ts";
import type { BuilderTab } from "../../../store/types/uiSettingsState.types.ts";
import {
  createCurrentReportAction,
  createFinancialStatementAction,
  createUiSettingsAction,
} from "../../../store/utils/storeUtils.ts";
import { getDimensionsAllowedInConditions } from "../../../utilities/conditionDataTypes.ts";
import DimensionSelection from "../../viewer/common/DimensionSelection.tsx";
import type { TableComponentProps } from "../../viewer/common/Types.ts";
import { createConditionField } from "../common/utilities/createFields.ts";
import ConditionsDropFieldsContainer from "../pivot/table/conditions/ConditionsDropFieldsContainer.tsx";
import AxesArea from "./columns/AxesArea.tsx";
import DataColumnsArea from "./columns/DataColumnsArea.tsx";
import FinancialStatementFormatContainer from "./format/FinancialStatementFormatContainer.tsx";
import RowsArea from "./rows/RowsArea.tsx";
import FinancialStatementSettingsContainer from "./settings/FinancialStatementSettingsContainer.tsx";

const FinancialStatementTableComponent = ({ ReportTypeSelector }: TableComponentProps) => {
  const dispatch = useAppDispatch();
  const { dimensions, conditions, reportType } = useBiCommonDataContext();
  const selectedTab = useSelector(selectSelectedBuilderTab);
  const editDisabled = useSelector(selectEditDisabled);
  const dimensionsStructure = useSelector(selectDimensionsStructureForCurrentReportType);
  const [addConditionAnchorEl, setAddConditionAnchorEl] = useState<HTMLElement>();

  const handleAddConditionClose = (dimension?: DimensionDescriptor) => {
    setAddConditionAnchorEl(undefined);
    if (!dimension) {
      return;
    }
    const field = createConditionField(dimension);
    dispatch(createFinancialStatementAction("addCondition", { field, index: conditions.length }));
    dispatch(createCurrentReportAction("setAutoShowItemOptionsId", field.config.guid));
  };

  const conditionDimensions = getDimensionsAllowedInConditions(dimensions, reportType).filter(
    (dimension) => !conditions.some((condition) => condition.meta.name === dimension.name)
  );

  return (
    <Stack sx={{ flex: 1, gap: "1rem", mt: "1rem" }}>
      <Box sx={{ px: "1.3rem" }}>
        {ReportTypeSelector}
        <Tabs
          value={selectedTab}
          sx={{ borderBottom: "none" }}
          onChange={(_, value: BuilderTab) => dispatch(createUiSettingsAction("setTab", value))}
        >
          <Tab value="Fields" label="Fields" />
          <Tab value="Format" label="Format" />
          <Tab value="Settings" label="Settings" />
        </Tabs>
      </Box>
      <Divider sx={{ mt: -2 }} />
      {selectedTab === "Fields" && (
        <ScrollableFlexContainer
          scrollContainerSx={{ display: "flex", flexDirection: "column", gap: "1.5rem", px: "1.3rem" }}
        >
          <ConditionsDropFieldsContainer
            disableEqualitySelection
            readonlyMode={editDisabled}
            addFieldAction={{ label: "Add Condition...", onClick: setAddConditionAnchorEl }}
          />
          {addConditionAnchorEl && (
            <DimensionSelection
              anchorEl={addConditionAnchorEl}
              dimensions={conditionDimensions}
              menuWidth={addConditionAnchorEl.offsetWidth}
              dimensionsStructure={dimensionsStructure}
              onClose={handleAddConditionClose}
            />
          )}
          <RowsArea readonlyMode={editDisabled} />
          <AxesArea readonlyMode={editDisabled} />
          <DataColumnsArea readonlyMode={editDisabled} />
        </ScrollableFlexContainer>
      )}
      {selectedTab === "Format" && <FinancialStatementFormatContainer readonlyMode={editDisabled} />}
      {selectedTab === "Settings" && <FinancialStatementSettingsContainer readonlyMode={editDisabled} />}
    </Stack>
  );
};

export default FinancialStatementTableComponent;
